import * as React from 'react';
import {
  Box,
  StackLayout,
  ParagraphText,
  GDSSpaceToken,
  spacingTokenMap,
  visuallyHiddenStyle,
  type GDSHeadingLevel,
  type GDSSpace,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';
import { MasonryDriverAction } from '../../types/masonry-driver-node-actions';
import { SectionHeader } from '../masonry-section-header-renderer/section-header.component';
import { GridContainer } from '../../common/grid-container/grid-container';
import { MasonryTextAction } from '../common/masonry-text-action/masonry-text-action.component';

export interface MasonryGridLayoutNodeProperties {
  body?: string;
  columnCount: number;
  heading?: string;
  headingLevel?: GDSHeadingLevel;
  isSemanticList?: boolean;
  spacing?: GDSSpaceToken;
  headerButtonLabel?: string;
  minGridItemWidth?: string;
  mobileMinGridItemWidth?: string;
  sectionHeaderPaddingTop?: GDSSpace;
}

export type MasonryGridLayoutNodeActions = {
  onHeaderButtonClick?: MasonryDriverAction;
};

export type MasonryGridLayoutNode = MasonryEngineNode<
  'gridLayout',
  MasonryGridLayoutNodeProperties,
  MasonryGridLayoutNodeActions,
  {
    items: MasonryEngineNode<string, any>[];
  }
>;

export type MasonryGridLayoutRendererProps =
  MasonryEngineNodeRendererProps<MasonryGridLayoutNode>;

export const MasonryGridLayoutRenderer = ({
  body,
  columnCount = 2,
  heading,
  headingLevel = '2',
  isSemanticList,
  items,
  headerButtonLabel,
  onHeaderButtonClick,
  spacing = 'one',
  className,
  engineClassName,
  rootElementRef,
  locatorId,
  css,
  minGridItemWidth,
  mobileMinGridItemWidth,
  sectionHeaderPaddingTop,
}: MasonryGridLayoutRendererProps) => {
  const hasTextAction: boolean = !!onHeaderButtonClick && !!headerButtonLabel;

  if (!items) return null;
  const gridSpacing = spacingTokenMap[spacing];

  return (
    <StackLayout
      spacing="$one"
      horizontalAlignment="stretch"
      orientation="vertical"
      className={formatClassNames(engineClassName, className)}
      ref={rootElementRef}
      data-testid={locatorId}
      css={css}
    >
      {heading && (
        <SectionHeader
          paddingTop={sectionHeaderPaddingTop}
          textAction={
            hasTextAction ? (
              <MasonryTextAction
                onClick={onHeaderButtonClick}
                text={headerButtonLabel}
              />
            ) : undefined
          }
          heading={heading}
          headingLevel={headingLevel}
        />
      )}
      {body && <ParagraphText>{body}</ParagraphText>}
      {items && (
        <GridContainer
          as={isSemanticList ? 'ul' : 'div'}
          spacing={gridSpacing}
          gridColumnCount={columnCount}
          minGridItemWidth={minGridItemWidth}
          mobileMinGridItemWidth={mobileMinGridItemWidth}
        >
          {isSemanticList
            ? React.Children.map(
                items,
                (item) =>
                  item && (
                    <Box
                      as="li"
                      css={{
                        '&:empty': {
                          display: 'none',
                        },
                        '&:has(> .visually-hidden)': {
                          ...visuallyHiddenStyle,
                        },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        '& > *': {
                          flex: 1,
                        },
                      }}
                    >
                      {item}
                    </Box>
                  ),
              )
            : items}
        </GridContainer>
      )}
    </StackLayout>
  );
};
